import React, { useState, useEffect } from "react";
import { Calendar, MessageCircle, Video, Check, ArrowRight, Clock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingContact from "../components/FloatingContact";
import ApplicationDialog from "../components/ApplicationDialog";
import { useLang } from "../i18n/LangContext";

const NAVY = "#1B2A4A";
const GOLD = "#C9A96E";
const CREAM = "#FAF8F5";

const I18N = {
  uk: {
    kicker: "БЕЗКОШТОВНА КОНСУЛЬТАЦІЯ",
    h1: "Запишіться на консультацію з експертом STUDYGRI",
    sub: "45 хвилин один на один з експертом. Розберемо ваш кейс, підберемо країну, школу чи університет та складемо покроковий план вступу.",
    cta: "Записатися зараз",
    whatYouGetTitle: "Що ви отримаєте на консультації",
    steps: [
      { icon: Check, t: "Аналіз вашого кейсу", d: "Оцінимо академічний рівень, бюджет, цілі та побажання щодо країни." },
      { icon: Check, t: "Підбір країн і закладів", d: "Порекомендуємо 3-5 варіантів шкіл чи університетів, які підходять саме вам." },
      { icon: Check, t: "Покроковий план вступу", d: "Розпишемо всі етапи: документи, іспити, дедлайни, вартість." },
      { icon: Check, t: "Відповіді на всі питання", d: "Про візи, стипендії, проживання, супровід під час навчання." }
    ],
    formatsTitle: "Формат зустрічі",
    formats: [
      { icon: Video, t: "Онлайн — Zoom / Google Meet", d: "Зручно з будь-якої точки світу. Отримаєте посилання на пошту." },
      { icon: MessageCircle, t: "Офлайн — офіс у Києві", d: "Особиста зустріч у нашому офісі за попереднім записом." },
      { icon: Calendar, t: "45 хвилин", d: "Достатньо часу, щоб розібратись і відповісти на всі ваші питання." }
    ],
    priceLine: "Консультація безкоштовна · Без зобов'язань",
    trustLine: "12 років на ринку · 14 000 клієнтів · 97% вступають до пріоритетного закладу"
  },
  ru: {
    kicker: "БЕСПЛАТНАЯ КОНСУЛЬТАЦИЯ",
    h1: "Запишитесь на консультацию с экспертом STUDYGRI",
    sub: "45 минут один на один с экспертом. Разберём ваш кейс, подберём страну, школу или университет и составим пошаговый план поступления.",
    cta: "Записаться сейчас",
    whatYouGetTitle: "Что вы получите на консультации",
    steps: [
      { icon: Check, t: "Анализ вашего кейса", d: "Оценим академический уровень, бюджет, цели и пожелания по стране." },
      { icon: Check, t: "Подбор стран и заведений", d: "Порекомендуем 3-5 вариантов школ или университетов, которые подходят именно вам." },
      { icon: Check, t: "Пошаговый план поступления", d: "Распишем все этапы: документы, экзамены, дедлайны, стоимость." },
      { icon: Check, t: "Ответы на все вопросы", d: "О визах, стипендиях, проживании, сопровождении во время учёбы." }
    ],
    formatsTitle: "Формат встречи",
    formats: [
      { icon: Video, t: "Онлайн — Zoom / Google Meet", d: "Удобно из любой точки мира. Ссылку пришлём на почту." },
      { icon: MessageCircle, t: "Офлайн — офис в Киеве", d: "Личная встреча в нашем офисе по предварительной записи." },
      { icon: Calendar, t: "45 минут", d: "Достаточно, чтобы разобраться и ответить на все ваши вопросы." }
    ],
    priceLine: "Консультация бесплатная · Без обязательств",
    trustLine: "12 лет на рынке · 14 000 клиентов · 97% поступают в приоритетное заведение"
  },
  en: {
    kicker: "FREE CONSULTATION",
    h1: "Book a consultation with a STUDYGRI expert",
    sub: "45 minutes one-on-one with an expert. We'll review your case, pick a country, school or university and build a step-by-step admission plan.",
    cta: "Book now",
    whatYouGetTitle: "What you'll get from the consultation",
    steps: [
      { icon: Check, t: "Case analysis", d: "We'll assess academic level, budget, goals and country preferences." },
      { icon: Check, t: "Country and school selection", d: "We'll recommend 3-5 schools or universities that fit you." },
      { icon: Check, t: "Step-by-step admission plan", d: "We'll outline all stages: documents, exams, deadlines, costs." },
      { icon: Check, t: "Answers to all questions", d: "About visas, scholarships, accommodation, support during studies." }
    ],
    formatsTitle: "Meeting format",
    formats: [
      { icon: Video, t: "Online — Zoom / Google Meet", d: "Convenient from anywhere in the world. Link sent to your email." },
      { icon: MessageCircle, t: "Offline — office in Kyiv", d: "In-person meeting at our office by appointment." },
      { icon: Calendar, t: "45 minutes", d: "Enough time to figure out and answer all your questions." }
    ],
    priceLine: "Consultation is free · No obligation",
    trustLine: "12 years on the market · 14,000 clients · 97% enroll in a priority institution"
  }
};

export default function ConsultationPage() {
  const { lang } = useLang();
  const t = I18N[lang] || I18N.en;
  const [formOpen, setFormOpen] = useState(false);

  // Auto-open the dialog after a short delay so people who click a shared link see the form immediately
  useEffect(() => {
    const timer = setTimeout(() => setFormOpen(true), 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: CREAM }}>
      <Header onOpenForm={() => setFormOpen(true)} />
      <main className="pt-28 md:pt-36 pb-20" data-testid="consultation-page">
        {/* HERO */}
        <section className="relative overflow-hidden pb-14 md:pb-20 px-5 md:px-8" style={{ background: NAVY, color: '#fff' }}>
          <div className="max-w-4xl mx-auto text-center pt-14 md:pt-20">
            <div className="inline-block text-[11px] font-bold tracking-[0.28em] uppercase mb-5 px-4 py-1.5 rounded-full" style={{ background: GOLD, color: NAVY }}>{t.kicker}</div>
            <h1 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.1] text-balance" style={{ fontWeight: 900, letterSpacing: '-0.025em' }}>{t.h1}</h1>
            <p className="mt-6 text-base md:text-xl max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.85)' }}>{t.sub}</p>
            <button onClick={() => setFormOpen(true)} data-testid="cp-hero-cta" className="mt-9 inline-flex items-center gap-2 px-8 h-13 py-3.5 rounded-full font-bold text-base transition hover:opacity-90" style={{ background: GOLD, color: NAVY }}>
              {t.cta} <ArrowRight className="w-4 h-4" />
            </button>
            <div className="mt-4 text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>{t.priceLine}</div>
          </div>
        </section>

        {/* WHAT YOU GET */}
        <section className="max-w-5xl mx-auto px-5 md:px-8 -mt-10 md:-mt-14">
          <div className="rounded-3xl p-7 md:p-10" style={{ background: '#fff', border: '1px solid #E8E0D5', boxShadow: '0 30px 80px -30px rgba(27,42,74,0.15)' }}>
            <h2 className="font-display text-2xl md:text-3xl text-center mb-8" style={{ color: NAVY, fontWeight: 900 }}>{t.whatYouGetTitle}</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {t.steps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className="flex gap-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(201,169,110,0.15)' }}>
                      <Icon className="w-5 h-5" style={{ color: GOLD }} />
                    </div>
                    <div>
                      <div className="font-display text-lg mb-1" style={{ color: NAVY, fontWeight: 900 }}>{s.t}</div>
                      <div className="text-sm" style={{ color: '#666' }}>{s.d}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FORMATS */}
        <section className="max-w-5xl mx-auto px-5 md:px-8 mt-12">
          <h2 className="font-display text-2xl md:text-3xl text-center mb-8" style={{ color: NAVY, fontWeight: 900 }}>{t.formatsTitle}</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {t.formats.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="rounded-2xl p-6 text-center" style={{ background: '#fff', border: '1px solid #E8E0D5' }}>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: NAVY }}>
                    <Icon className="w-5 h-5" style={{ color: GOLD }} />
                  </div>
                  <div className="font-display text-lg mb-1" style={{ color: NAVY, fontWeight: 900 }}>{f.t}</div>
                  <div className="text-sm" style={{ color: '#666' }}>{f.d}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* TRUST */}
        <section className="max-w-3xl mx-auto text-center mt-16 px-5">
          <p className="text-sm" style={{ color: '#666' }}>{t.trustLine}</p>
          <button onClick={() => setFormOpen(true)} data-testid="cp-bottom-cta" className="mt-6 inline-flex items-center gap-2 px-8 h-13 py-3.5 rounded-full font-bold text-base transition hover:opacity-90" style={{ background: NAVY, color: '#fff' }}>
            {t.cta} <ArrowRight className="w-4 h-4" />
          </button>
        </section>
      </main>
      <Footer />
      <FloatingContact onOpenForm={() => setFormOpen(true)} />
      <ApplicationDialog open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
}
